import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../../api/apiClient';

const ITEM_ICONS = {
  call: '/Home/phone.svg',
  phone: '/Home/phone.svg',
  email: '/Home/mail.svg',
  location: '/Home/location.svg',
};

const SOCIAL_ICONS = {
  facebook: '/Home/facebook.svg',
  instagram: '/Home/instagram.svg',
  youtube: '/Home/youtube.svg',
  twitter: '/Home/twitter.svg',
  linkedin: '/Home/linkedin.svg',
  location: '/Home/location.svg',
};

const isActive = (i) => i.status !== 0;
const byOrder = (arr) => [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

// If an item has a label, only show an icon when that exact label is in
// the map ("Call", "Phone", "Email", "Location"). Anything else with a
// label — like "Office hours" — renders with no icon, no guessing.
// Only truly unlabeled items (icon-only bars) fall back to guessing an
// icon from the shape of the value.
function iconFor(item, map) {
  const key = (item.label || '').trim().toLowerCase();
  if (key) {
    return map[key]; // undefined if not found — correctly renders no icon
  }
  const v = item.value || '';
  if (v.includes('@')) return map.email;
  if (/^[+\d]/.test(v)) return map.phone;
  return map.location;
}

/* ────────────────────────────────────────────────────────────────────────
 * Fallback content — used whenever the API call fails, returns a non-OK
 * status, or returns a payload with no usable topbar section. Shaped
 * identically to the live API response (items / rightSlot) so the render
 * logic below never has to branch on where the data came from.
 * ──────────────────────────────────────────────────────────────────────── */

const FALLBACK_TOPBAR = {
  items: [
    { id: 'call', label: 'Call', value: '08043694807', order: 0, status: 1 },
    { id: 'hours', label: 'Office hours', value: 'Mon-Fri : 9am-5pm', order: 1, status: 1 },
  ],
  rightSlot: {
    mode: 'appBanner',
    appBanner: {
      label: 'Experience AVEXA app on mobile',
      playStoreLink: '#',
      appStoreLink: '#',
    },
    socialLinks: [],
  },
};

const TopBar = () => {
  const [topbar, setTopbar] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether we're showing fallback content because the API call
  // failed or returned nothing usable. Not required for rendering — handy
  // if you want a subtle "offline" indicator later.
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    setTopbar(FALLBACK_TOPBAR);
    setUsingFallback(true);
    setLoading(false);
    // let cancelled = false;

    // (async () => {
    //   try {
    //     const res = await fetch(`${BASE_URL}/api/femcare/`);
    //     const json = await res.json().catch(() => ({}));

    //     if (!res.ok) {
    //       throw new Error(json?.error || `Request failed (${res.status})`);
    //     }

    //     const fetchedTopbar = json?.topbar;

    //     // Treat a missing/empty payload the same as a failed request —
    //     // fall back rather than rendering nothing.
    //     if (!fetchedTopbar || !Array.isArray(fetchedTopbar.items) || fetchedTopbar.items.length === 0) {
    //       throw new Error('Topbar payload missing or empty');
    //     }

    //     if (!cancelled) {
    //       setTopbar(fetchedTopbar);
    //       setUsingFallback(false);
    //     }
    //   } catch (e) {
    //     console.error('TopBar: falling back to default content —', e.message);
    //     if (!cancelled) {
    //       setTopbar(FALLBACK_TOPBAR);
    //       setUsingFallback(true);
    //     }
    //   } finally {
    //     if (!cancelled) setLoading(false);
    //   }
    // })();

    // return () => {
    //   cancelled = true;
    // };
  }, []);

  // Only genuinely nothing-to-show while the very first fetch attempt is
  // still in flight. Once it settles (success or failure) `topbar` is
  // always populated, either from the API or from FALLBACK_TOPBAR.
  if (loading || !topbar) return null;

  const items = byOrder(topbar.items || []).filter(isActive);
  const rightSlot = topbar.rightSlot;
  const mode = rightSlot?.mode ?? 'appBanner';
  const socialLinks = byOrder(rightSlot?.socialLinks || []).filter(isActive);

  return (
    <div className="primary-bg w-full">
      <div className="px-4 md:px-8">
        <div className="flex items-center justify-between h-10">

          {/* Left: items — renders "Label : value" if label is set, else just the icon + value */}
          <div className="flex items-center gap-1 text-white ava-text-sm flex-wrap">
            {items.map((item, idx) => {
              const icon = iconFor(item, ITEM_ICONS);
              return (
                <React.Fragment key={item.id}>
                  {idx > 0 && <span className="mx-2 opacity-60">-</span>}
                  <span className="flex items-center gap-1">
                    {icon && <img src={icon} alt={item.label || 'icon'} className="w-3.5 h-3.5 flex-shrink-0" />}
                    <span className={idx === 0 ? '' : 'hidden sm:inline'}>
                      {item.label ? `${item.label} : ${item.value}` : item.value}
                    </span>
                  </span>
                </React.Fragment>
              );
            })}
          </div>

          {/* Right: app banner or social/utility icons */}
          <div className="flex items-center gap-2 text-white ava-text-sm">
            {mode === 'appBanner' ? (
              <>
                <span className="hidden sm:inline">{rightSlot?.appBanner?.label}</span>
                <span className="sm:hidden">Get the App</span>

                <a
                  href={rightSlot?.appBanner?.playStoreLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center hover:opacity-80 transition-opacity"
                  aria-label="Download on Google Play"
                >
                  <img src="/Home/playstore.svg" alt="Google Play" className="h-5 w-5" />
                </a>

                <a
                  href={rightSlot?.appBanner?.appStoreLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center hover:opacity-80 transition-opacity"
                  aria-label="Download on App Store"
                >
                  <img src="/Home/applestore.svg" alt="App Store" className="h-5 w-5" />
                </a>
              </>
            ) : (
              socialLinks.map((link) => {
                const icon = iconFor(link, SOCIAL_ICONS);
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center hover:opacity-80 transition-opacity"
                    aria-label={link.label}
                  >
                    {icon ? <img src={icon} alt={link.label} className="h-5 w-5" /> : <span className="text-xs">{link.label}</span>}
                  </a>
                );
              })
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default TopBar;